import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Box,  Container, Skeleton, Chip, Stack, Divider, Avatar } from '@mui/material';
import CoverLayout from 'layouts/authentication/components/CoverLayout';
import Navbar from 'layouts/Homepage/navbar';
import Footer from 'layouts/Homepage/Footer';
import MDTypography from 'components/MDTypography';

const BlogPostPage = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

useEffect(() => {
  setLoading(true);
  axios
    .get(`https://blog.saleshive.cloud/wp-json/wp/v2/posts?slug=${slug}&_embed`)
    .then(async (res) => {
      if (res.data.length > 0) {
        const postData = res.data[0];

        // If author data is not embedded, fetch it manually
        if (!postData._embedded?.author?.[0] && postData.author) {
          try {
            const authorRes = await axios.get(
              `https://blog.saleshive.cloud/wp-json/wp/v2/users/${postData.author}`
            );
            postData._embedded = {
              ...(postData._embedded || {}),
              author: [authorRes.data],
            };
          } catch (err) {
            console.warn("Author fetch failed", err);
          }
        }

        setPost(postData);
      }
    })
    .catch((err) => console.error("Failed to load post", err))
    .finally(() => setLoading(false));
}, [slug]);

  if (loading) {
    return (
      <CoverLayout>
        <Navbar />
        <Container maxWidth="md" sx={{ py: 6 }}>
          <Skeleton variant="text" width="30%" height={30} sx={{ mb: 2 }} />
          <Skeleton variant="text" width="80%" height={60} sx={{ mb: 2 }} />
          <Box display="flex" alignItems="center" mb={2}>
            <Skeleton variant="circular" width={36} height={36} sx={{ mr: 1 }} />
            <Skeleton variant="text" width={100} height={24} />
          </Box>
          <Skeleton variant="rectangular" width="100%" height={320} sx={{ mb: 3, borderRadius: 3 }} />
          <Skeleton variant="text" width="100%" height={40} sx={{ mb: 1 }} />
          <Skeleton variant="text" width="90%" height={40} sx={{ mb: 1 }} />
          <Skeleton variant="text" width="80%" height={40} />
        </Container>
        <Footer />
      </CoverLayout>
    );
  }

  if (!post) {
    return (
      <CoverLayout>
        <Navbar />
        <Container maxWidth="md" sx={{ py: 6 }}>
          <MDTypography variant="h5" color="error" align="center">
            Blog post not found.
          </MDTypography>
        </Container>
        <Footer />
      </CoverLayout>
    );
  }

  const categories = post._embedded?.['wp:term']?.[0] || [];
  const author = post._embedded?.author?.[0];

  // Try to extract date and reading time if available
  const postDate = post.date ? new Date(post.date).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: '2-digit' }) : '';
  // Simple reading time estimate: 200 words/minute
  const wordCount = post.content?.rendered ? post.content.rendered.replace(/<[^>]+>/g, '').split(/\s+/).length : 0;
  const readingTime = wordCount ? Math.max(1, Math.round(wordCount / 200)) : null;

  return (
    <CoverLayout>
      <Navbar />
      <Container maxWidth="md" sx={{ py: { xs: 3, md: 6 } }}>
        <Box sx={{ mb: 2 }}>
          
          <MDTypography
            variant="h2"
            fontWeight={700}
            sx={{
              mb: 2,
              color: "text.dark",
              lineHeight: 1.15,
              fontSize: { xs: "2rem", md: "2.8rem" },
            }}
            dangerouslySetInnerHTML={{ __html: post.title.rendered }}
          />
          <Box display="flex" alignItems="center" mb={2}>
            {author?.avatar_urls?.['48'] && (
              <Avatar
                src={author.avatar_urls['48']}
                alt={author.name || "Author"}
                sx={{ width: 36, height: 36, mr: 1 }}
              />
            )}
            <MDTypography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
              {author?.name || "Unknown Author"}
              {postDate && (
                <>
                  {' '} &bull; <span style={{ color: "#888" }}>Posted: {postDate}</span>
                </>
              )}
              {readingTime && (
                <>
                  {' '} &bull; <span style={{ color: "#888" }}>{readingTime} mins read</span>
                </>
              )}
            </MDTypography>
          </Box>
        </Box>
        {post._embedded?.['wp:featuredmedia'] && (
          <Box
            display="flex"
            justifyContent="center"
            my={3}
          >
            <Box
              component="img"
              src={post._embedded['wp:featuredmedia'][0].source_url}
              alt={post.title.rendered}
              sx={{
                width: '100%',
                maxWidth:"50%",
                borderRadius: 4,
                boxShadow: 4,
                objectFit: "cover",
                display: "block",
              }}
            />
          </Box>
        )}
        {/* <Box
          sx={{
            // background: "#fff",
            // borderRadius: 4,
            boxShadow: 2,
            p: { xs: 2, md: 4 },
            mb: 4,
            fontSize: "1.15rem",
            color: "text.primary",
            lineHeight: 1.8,
            maxWidth: "100%",
            overflowX: "auto",
            border:0,
          }}
        > */}
          <MDTypography
            variant="body1"
            component="div"
            dangerouslySetInnerHTML={{ __html: post.content.rendered }}
            sx={{
              "& img": {
                maxWidth: "100%",
                borderRadius: 2,
                my: 2,
              },
              "& h1, & h2, & h3, & h4": {
                mt: 3,
                mb: 1,
                fontWeight: 600,
              },
              "& p": {
                mb: 2,
              },
              "& ul, & ol": {
                pl: 3,
                mb: 2,
              },
              "& blockquote": {
                borderLeft: "4px solid #1976d2",
                pl: 2,
                color: "grey.700",
                fontStyle: "italic",
                // background: "#f5faff",
                my: 2,
                py: 1,
              },
              "& pre, & code": {
                // background: "#f4f4f4",
                borderRadius: 2,
                px: 1,
                py: 0.5,
                fontSize: "0.95em",
                fontFamily: "monospace",
                color: "#333",
              },
              "& table": {
                width: "100%",
                borderCollapse: "collapse",
                my: 2,
              },
              "& th, & td": {
                border: "1px solid #e0e0e0",
                px: 1,
                py: 0.5,
              },
            }}
          />
        {/* </Box> */}
      </Container>
      <Footer />
    </CoverLayout>
  );
};

export default BlogPostPage;
